import accounting from 'accounting-js';
import { EUR } from '../../constants';
import {
  GlobalForecastSubPortfolio,
  OpexCapexAmounts,
} from '../../store/financialFrames/stateType';

export type AmountRowKey = 'opex' | 'capex' | 'total';

export const AMOUNT_ROWS: AmountRowKey[] = ['opex', 'capex', 'total'];

export const toKeur = (value: number | null | undefined): number | null =>
  value === null || value === undefined ? null : value / 1000;

export const formatKeur = (value: number | null | undefined): string => {
  const keur = toKeur(value);
  return keur === null
    ? '-'
    : accounting.formatMoney(keur, {
        precision: 0,
        symbol: `K${EUR}`,
        format: '%v %s',
      });
};

export const formatSignedKeur = (value: number | null | undefined): string => {
  const keur = toKeur(value);
  if (keur === null) return '-';
  const formatted = accounting.formatNumber(keur, { precision: 0 });
  return keur > 0 ? `+${formatted}` : formatted;
};

export const getAmount = (
  amounts: OpexCapexAmounts | null | undefined,
  key: AmountRowKey,
): number | null => amounts?.[key]?.eur ?? null;

const sumNullable = (a: number | null, b: number | null): number | null =>
  a === null && b === null ? null : (a ?? 0) + (b ?? 0);

const subtractNullable = (a: number | null, b: number | null): number | null =>
  a === null && b === null ? null : (a ?? 0) - (b ?? 0);

const withTotal = (opex: number | null, capex: number | null) => ({
  opex,
  capex,
  total: sumNullable(opex, capex),
});

export type ForecastRow = {
  key: AmountRowKey;
  allocated: number | null;
  reported: number | null;
  diff: number | null;
  actuals: number | null;
};

const buildRows = (
  allocated: Record<AmountRowKey, number | null>,
  reported: Record<AmountRowKey, number | null>,
  actuals: Record<AmountRowKey, number | null>,
): ForecastRow[] =>
  AMOUNT_ROWS.map((key) => ({
    key,
    allocated: allocated[key],
    reported: reported[key],
    diff: subtractNullable(allocated[key], reported[key]),
    actuals: actuals[key],
  }));

const amountsWithTotal = (amounts: OpexCapexAmounts | null | undefined) =>
  withTotal(getAmount(amounts, 'opex'), getAmount(amounts, 'capex'));

export const getForecastRows = (
  entry: GlobalForecastSubPortfolio | undefined,
): ForecastRow[] =>
  buildRows(
    amountsWithTotal(entry?.allocatedFrame),
    amountsWithTotal(entry?.reportedForecast),
    amountsWithTotal(entry?.actuals),
  );

export const getReportedValueRows = (
  entry: GlobalForecastSubPortfolio,
): ForecastRow[] => {
  const adjustment = entry.latestAdjustment;
  return buildRows(
    amountsWithTotal(entry.allocatedFrame),
    withTotal(
      sumNullable(adjustment.reportedOpex, adjustment.opex),
      sumNullable(adjustment.reportedCapex, adjustment.capex),
    ),
    amountsWithTotal(entry.actuals),
  );
};

export const formatSignedNumber = (numString: string): string => {
  const sign = /^[+-]/.test(numString) ? numString[0] : '';
  const digits = numString.slice(sign.length);
  return sign + digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const removeSignedFormatting = (input: string): string => {
  const sign = /^[^\d]*?([+-])/.exec(input)?.[1] ?? '';
  const digits = input.replace(/\D/g, '').replace(/^0+(?=\d)/, '');
  return sign + digits;
};

export const keurInputToEur = (value: string): number | null => {
  if (value === '' || value === '-' || value === '+') return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : Math.round(parsed * 1000);
};
